/**
 * Vocabulary Sets Screen (route `VocabSets`)
 *
 * Themed word sets — colours, months, body parts — as a second route to the
 * same senses role play already teaches. A learner who wants the twelve months
 * should not have to find a scene that happens to contain them.
 *
 * Progress is shown as COUNTS, never a percentage (VSM-002): "8/12" tells the
 * learner how many words are left; "67%" does not.
 *
 * A set owns no progress of its own — every count derives from the shared
 * `UserVocabUsage` rows (VSET-002), so a word mastered in a role play already
 * reads as mastered here the first time this screen is opened.
 *
 * @see docs/specs/mobile/vocab-sets.mobile.md
 */

import React, { useCallback } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import { ScreenHeader } from '../components/ui/ScreenHeader';
import { WebContainer } from '../components/ui/WebContainer';
import { ErrorState, LoadingState } from '../components/ui';
import { FS_SM, FS_MD } from '../theme';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { useResolveBilingual } from '../hooks/useResolveBilingual';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import { errorMessage } from '../generated/api/endpoint-errors';
import {
  selectLoadVocabSets,
  selectVocabSets,
  selectVocabSetsErrorInfo,
  selectVocabSetsLoading,
  useVocabSetsStore,
} from '../store/vocabSetsStore';
import type { VocabSetSummary } from '../generated/api/http-contracts';

export function VocabSetsScreen() {
  const { t } = useTranslation();
  const { theme } = useDisplayTheme();
  const navigation = useNavigation();
  const resolve = useResolveBilingual();
  const courseId = useUserPreferencesStore((s) => s.activeCourseId);

  const sets = useVocabSetsStore(selectVocabSets);
  const loading = useVocabSetsStore(selectVocabSetsLoading);
  const errorInfo = useVocabSetsStore(selectVocabSetsErrorInfo);
  const loadSets = useVocabSetsStore(selectLoadVocabSets);

  useFocusEffect(
    useCallback(() => {
      if (courseId) void loadSets(courseId);
    }, [loadSets, courseId]),
  );

  const open = useCallback(
    (slug: string) => {
      (navigation.navigate as (n: string, p?: object) => void)('VocabSetDrill', { slug });
    },
    [navigation],
  );

  return (
    <>
      <ScreenHeader title={t('vocabSets.title')} />
      <WebContainer variant="page" style={{ flex: 1 }}>
        {loading && sets.length === 0 ? (
          <LoadingState testID="vocab-sets-loading" message={t('vocabSets.loading')} />
        ) : errorInfo && sets.length === 0 ? (
          <ErrorState
            testID="vocab-sets-error"
            message={errorInfo ? errorMessage(errorInfo) : t('vocabSets.loadError')}
            retryLabel={t('common.retry')}
            onRetry={() => {
              if (courseId) void loadSets(courseId);
            }}
          />
        ) : (
          <ScrollView contentContainerStyle={styles.content} testID="vocab-sets-scroll">
            <Text style={[styles.intro, { color: theme.textSecondary }]}>
              {t('vocabSets.intro')}
            </Text>
            {sets.map((s) => (
              <SetCard
                key={s.slug}
                set={s}
                title={resolve(s.title)}
                label={t('vocabSets.countLabel', {
                  done: s.progress.mastered,
                  total: s.progress.total,
                })}
                onPress={open}
              />
            ))}
          </ScrollView>
        )}
      </WebContainer>
    </>
  );
}

interface SetCardProps {
  set: VocabSetSummary;
  title: string;
  label: string;
  onPress: (slug: string) => void;
}

/** React.memo + stable callback — the list-item rule from CHAT_PERFORMANCE.md. */
const SetCard = React.memo(function SetCard({ set, title, label, onPress }: SetCardProps) {
  const { theme } = useDisplayTheme();
  const handle = useCallback(() => onPress(set.slug), [onPress, set.slug]);

  return (
    <TouchableOpacity
      onPress={handle}
      testID={`vocab-sets-card-${set.slug}`}
      accessibilityRole="button"
      accessibilityLabel={`${title}. ${label}`}
      style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}
    >
      <Text style={styles.emoji}>{set.emoji ?? '📘'}</Text>
      <View style={styles.cardBody}>
        <Text style={[styles.cardTitle, { color: theme.text }]} numberOfLines={1}>
          {title}
        </Text>
        {/* Counts, not a percentage (VSM-002). */}
        <Text style={[styles.cardCount, { color: theme.textSecondary }]}>{label}</Text>
      </View>
    </TouchableOpacity>
  );
});

const styles = StyleSheet.create({
  content: { padding: 16, paddingBottom: 40 },
  intro: { fontSize: FS_SM, lineHeight: 20, marginBottom: 16 },
  card: {
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    flexDirection: 'row',
    marginBottom: 10,
    padding: 14,
  },
  emoji: { fontSize: 26, marginRight: 14 },
  cardBody: { flex: 1 },
  cardTitle: { fontSize: FS_MD, fontWeight: '600' },
  cardCount: { fontSize: FS_SM, marginTop: 2 },
});
